/**
 * @file Precomputed direct visibility, one scalar channel per keyed sun
 * direction. This is the half of the bake that could not stay frozen once the
 * sun started moving.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS BAKED HERE AND WHAT IS NOT
 * ─────────────────────────────────────────────────────────────────────────────
 * `bakeVertexLight.js` splits its day channel in two:
 *
 *     day = ambient                                   <- direction-FREE, baked once
 *         + sunTint * max(dot(n, L), 0) * sunVis(L)   <- L moves, so this is live
 *
 * `ambient` (hemi + bounce + floor, all scaled by AO) and `sunTint`
 * (albedo x sunColor x sunIntensity) are both independent of where the sun is,
 * so they stay in the one-shot bake. `dot(n, L)` is three instructions in the
 * vertex stage. That leaves `sunVis` — a function of POSITION *and* DIRECTION,
 * and the only thing in the whole lighting model that genuinely has to be
 * resampled when the sun moves.
 *
 * So we sample it at {@link import('./sunArc.js').KEY_COUNT} directions across
 * the day and blend the two straddling the current hour.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE DEDUP IS PREPARED ONCE AND THE KEYS BAKED SEPARATELY
 * ─────────────────────────────────────────────────────────────────────────────
 * Measured on a 676-building / 413k-vertex city stand-in: a full bake is
 * ~580 ms, of which only ~106 ms is the sun term. The other ~475 ms — dedup,
 * AO, hemi, write-out — is direction-free and would be pure waste to repeat 29
 * times (8.7 s of re-baking against 2.3 s of re-solving). {@link SunKeySolver}
 * therefore pays the dedup ONCE in {@link prepareSunKeys} and then answers
 * {@link SunKeySolver#bakeKey} per direction.
 *
 * That split is also what makes progressive streaming possible: the level ships
 * with the key nearest its start hour and the worker posts the other 28 in
 * afterwards, so none of this lands on the loading screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TRAPS
 * ─────────────────────────────────────────────────────────────────────────────
 * • The dedup, the hash and the ray push are IMPORTED from `bakeVertexLight.js`,
 *   never re-implemented. A key baked at the level's own `sunDir` must reproduce
 *   that bake's `sunVis` bit for bit — `tests/sunkeys.test.mjs` asserts exactly
 *   that, and it is the test that catches any drift between the two paths.
 * • Visibility is stored `Uint8` (0..255 for 0..1). Unlike the LIT channels it
 *   can never exceed 1, so rule 5's "Float32 because bloom needs >1" does not
 *   apply here — and 1 byte per vertex per key is what keeps 15 keys affordable.
 *   ⚠ `world/Chunker.js` must preserve the array TYPE when it slices, or these
 *   silently widen to Float32 and cost 4x.
 * • Where `dot(n, L) <= 0` the stored value is 0. At runtime the live direction
 *   sits BETWEEN two keys, so a face right on the terminator can read a key that
 *   was already facing away. The error is O(dot(n, L)) — i.e. it vanishes
 *   exactly where it would be visible — so it is left alone deliberately.
 *
 * ⚠ ZERO three.js IMPORTS. `bake.worker.js` pulls this in.
 *
 * @module engine/bake/sunKeys
 */

import { DAY_DEFAULTS, mergeConfig } from './BakeConfig.js';
import {
  ORIGIN_PUSH,
  SUN_JITTER_SALT,
  normalAxis,
  rngFrom,
  slotKey,
  vertexHash,
} from './bakeVertexLight.js';

/** @typedef {import('./Occluders.js').OccluderSet} OccluderSet */
/** @typedef {{x: number, y: number, z: number}} V3 */

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * The dedup, solved once and reused by every key.
 *
 * Holds one entry per UNIQUE vertex (position, normal, ray seed) plus the
 * vertex -> slot map needed to scatter a solved key back out to the full vertex
 * array. Nothing here depends on the sun.
 */
export class SunKeySolver {
  /**
   * @param {Float32Array|ArrayLike<number>} position World-space, non-indexed.
   * @param {Float32Array|ArrayLike<number>|null} normal Per-face (3 identical
   *   per triangle), or null for +Y.
   * @param {any} cfg Merged bake config — only `dedupQuant` is read.
   */
  constructor(position, normal, cfg) {
    const n = (position?.length ?? 0) / 3;
    /** @type {number} Vertices in, and the length of every key this produces. */
    this.verts = n;

    const q = cfg.dedupQuant > 0 ? cfg.dedupQuant : 0;
    const invQ = q > 0 ? 1 / q : 0;
    const cache = q > 0 ? new Map() : null;

    /** @type {Int32Array} vertex -> unique slot */
    this.slotOf = new Int32Array(n);
    // Worst case is no dedup at all, so these are allocated full-size and
    // subarray'd down once the count is known. One allocation each, never in
    // the loop.
    const px = new Float32Array(n);
    const py = new Float32Array(n);
    const pz = new Float32Array(n);
    const nx = new Float32Array(n);
    const ny = new Float32Array(n);
    const nz = new Float32Array(n);
    const hash = new Uint32Array(n);

    let unique = 0;
    for (let i = 0; i < n; i++) {
      const p = i * 3;
      const x = position[p];
      const y = position[p + 1];
      const z = position[p + 2];
      const ax = normal ? normal[p] : 0;
      const ay = normal ? normal[p + 1] : 1;
      const az = normal ? normal[p + 2] : 0;

      const axis = normalAxis(ax, ay, az);
      let slot;
      if (cache) {
        const key = slotKey(x, y, z, axis, invQ);
        if (key >= 0) {
          slot = cache.get(key);
          if (slot === undefined) {
            slot = unique++;
            cache.set(key, slot);
          } else {
            this.slotOf[i] = slot;
            continue;
          }
        }
      }
      if (slot === undefined) slot = unique++;

      px[slot] = x;
      py[slot] = y;
      pz[slot] = z;
      nx[slot] = ax;
      ny[slot] = ay;
      nz[slot] = az;
      hash[slot] = vertexHash(x, y, z, axis);
      this.slotOf[i] = slot;
    }

    /** @type {number} Unique vertices — the number `bakeKey` actually solves. */
    this.unique = unique;
    this._px = px.subarray(0, unique);
    this._py = py.subarray(0, unique);
    this._pz = pz.subarray(0, unique);
    this._nx = nx.subarray(0, unique);
    this._ny = ny.subarray(0, unique);
    this._nz = nz.subarray(0, unique);
    this._hash = hash.subarray(0, unique);
    /** @type {Uint8Array} Scratch, one byte per unique. Reused every key. */
    this._solved = new Uint8Array(unique);
  }

  /**
   * Solves visibility for ONE sun direction.
   *
   * @param {V3} dir Surface -> sun, UNIT length (`sunArc.sunDirAt` guarantees it).
   * @param {OccluderSet|null} occ
   * @param {any} cfg Merged config — `sunRays`, `sunConeDeg`, `sunMaxDist`.
   * @returns {Uint8Array} Length `verts`. 0..255 maps to 0..1.
   */
  bakeKey(dir, occ, cfg) {
    const solved = this._solved;
    const empty = !occ || occ.count === 0;
    const cone = (cfg.sunConeDeg * Math.PI) / 180;
    const rays = cfg.sunRays;
    const maxT = cfg.sunMaxDist;

    for (let s = 0; s < this.unique; s++) {
      const ax = this._nx[s];
      const ay = this._ny[s];
      const az = this._nz[s];
      const ndl = ax * dir.x + ay * dir.y + az * dir.z;
      if (!(ndl > 0)) {
        solved[s] = 0;
        continue;
      }
      if (empty) {
        solved[s] = 255;
        continue;
      }
      const v = occ.sunVis(
        this._px[s] + ax * ORIGIN_PUSH,
        this._py[s] + ay * ORIGIN_PUSH,
        this._pz[s] + az * ORIGIN_PUSH,
        dir,
        { rays, cone, maxT, rng: rngFrom(this._hash[s] ^ SUN_JITTER_SALT) },
      );
      solved[s] = v <= 0 ? 0 : v >= 1 ? 255 : Math.round(v * 255);
    }

    const out = new Uint8Array(this.verts);
    const slotOf = this.slotOf;
    for (let i = 0; i < this.verts; i++) out[i] = solved[slotOf[i]];
    return out;
  }
}

/**
 * Builds the reusable dedup for a geometry's vertex arrays.
 *
 * @param {Float32Array|ArrayLike<number>} position
 * @param {Float32Array|ArrayLike<number>|null} normal
 * @param {Partial<import('../types.js').BakeConfig>|null} [config]
 * @returns {SunKeySolver}
 */
export function prepareSunKeys(position, normal, config = null) {
  const cfg = config && config.__merged ? config : mergeConfig(DAY_DEFAULTS, config);
  return new SunKeySolver(position, normal, cfg);
}

/**
 * Bakes every key in one call. The blocking path — used by tests, the dev
 * scenes and any caller that does not want to manage streaming.
 *
 * @param {Float32Array|ArrayLike<number>} position
 * @param {Float32Array|ArrayLike<number>|null} normal
 * @param {OccluderSet|null} occ
 * @param {V3[]} dirs From `sunArc.keyDirs()`.
 * @param {Partial<import('../types.js').BakeConfig>|null} [config]
 * @param {((done: number, total: number) => void)|null} [onKey] Called after
 *   each key; the worker turns it into a `postMessage`.
 * @returns {{keys: Uint8Array[], ms: number, verts: number, unique: number}}
 */
export function bakeSunKeys(position, normal, occ, dirs, config = null, onKey = null) {
  const t0 = now();
  const cfg = config && config.__merged ? config : mergeConfig(DAY_DEFAULTS, config);
  const solver = new SunKeySolver(position, normal, cfg);
  if (occ && typeof occ.build === 'function' && !occ._packed) occ.build({ cell: occ.cell ?? 4 });

  const keys = [];
  for (let k = 0; k < dirs.length; k++) {
    keys.push(solver.bakeKey(dirs[k], occ, cfg));
    if (onKey) onKey(k + 1, dirs.length);
  }
  return { keys, ms: now() - t0, verts: solver.verts, unique: solver.unique };
}

/** `THREE.DynamicDrawUsage`. Inlined rather than imported — see the header. */
const DYNAMIC_DRAW = 35048;

/**
 * Attaches the two RESIDENT visibility channels to a geometry as `color3`.
 *
 * Only two keys are ever on the GPU — the pair straddling the current hour —
 * packed `.r` = key `a`, `.g` = key `b`, and `render/nodes.js#sunTermNode`
 * mixes them by `uSunMix`. Swapping hours rewrites this one attribute rather
 * than binding a 29th buffer.
 *
 * ⚠ `itemSize` is 4, not the 2 the data needs. `vertexColor(3)` declares a
 * **vec4**, and handing WGSL a vec2 buffer for a vec4 attribute is a type
 * mismatch at pipeline build. Two wasted bytes per vertex is the price; it is
 * still 4 bytes against the 12 a Float32 vec3 would cost.
 *
 * @param {any} geo A `THREE.BufferGeometry` (duck-typed).
 * @param {Uint8Array} keyA
 * @param {Uint8Array} keyB
 * @returns {any} `geo`
 */
export function applySunVis(geo, keyA, keyB) {
  const pos = geo?.getAttribute?.('position');
  if (!pos) throw new Error('applySunVis: geometry has no `position` attribute');
  const n = pos.count;
  if (keyA.length !== n || keyB.length !== n) {
    throw new Error(`applySunVis: length mismatch — ${n} verts, got ${keyA.length} / ${keyB.length}`);
  }

  let attr = geo.getAttribute('color3');
  if (!attr || attr.count !== n) {
    // See the module header: built from the position attribute's constructor so
    // this file never imports three.
    const Ctor = /** @type {any} */ (pos.constructor);
    attr = new Ctor(new Uint8Array(n * 4), 4, true); // normalized: 255 -> 1.0
    attr.setUsage?.(DYNAMIC_DRAW);
    geo.setAttribute('color3', attr);
  }

  const a = attr.array;
  for (let i = 0; i < n; i++) {
    a[i * 4] = keyA[i];
    a[i * 4 + 1] = keyB[i];
  }
  attr.needsUpdate = true;
  return geo;
}
